import type { AccountUserGroup } from "@/enterpriseAccounts/visibility/accountVisibilityDomain";
import { makeAutoObservable } from "mobx";
import { pick } from "lodash";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms.ts";
import {
  DCEvent,
  HttpResponse,
  navigateTo,
  showGeneralError,
  showSuccess,
  thunk,
} from "@/events/eventsDomain.ts";
import { TMessage } from "@/i18n/tt.ts";
import { TMessageKey } from "@/i18n/types.gen";
import { Language } from "@/i18n/languages.ts";
import { LocalizedText } from "@/common/common.ts";
import { findLocalizedTextOrDefault } from "@/schedule/scheduleDomain.ts";
import { when } from "@/utils/functions.ts";
import { navigationState } from "@/utils/navigation.ts";
import {
  MapSettings,
  MapTemplateSetting,
  SaveTemplateContentRequest,
  TemplateDataDTO,
  TemplateDTO,
  TemplateOverviewDTO,
  TemplateStatus,
  templateStatusLabels,
  TemplateExclusions,
  TemplateType,
  templateTypeSegments,
  UpdateMapTemplateContentRequest,
  UpdateProjectTemplateContentRequest,
} from "@/templates/templateDomain";
import { getDecisionName } from "@/dbot/common/commonDomain";
import { getDocumentLabel } from "@/common/documents";
import { templateApiClient } from "@/templates/templateApiClient";

export type TemplateAttachment = { id: string; filename: string };
export type TemplateItem = {
  id: string;
  label: TMessage;
  attachments: TemplateAttachment[];
  isSealed: boolean;
};
export type TemplateAccount = { id: string; name: string };

export type TemplateDetailsState = {
  account: RadioFieldModel<TemplateAccount>;
  name: StringFieldModel;
  description: StringFieldModel;
  hasDescription: boolean;
  addDescription: () => void;
  removeDescription: () => void;
};

export type SaveAsTemplateDialogState = {
  type: "save-as-template";
  title: TMessage;
  submitLabel: TMessage;
  details: TemplateDetailsState;
  content: TemplateContentState;
  isSaving?: boolean;
  onSave: DCEvent[];
  onCancel: DCEvent[];
};

export type TemplateVisibilityDialogState = {
  type: "template-visibility";
  templateName: string;
  status: RadioFieldModel<TemplateStatus>;
  visibleTo: string[];
  groups: AccountUserGroup[];
  isSaving: boolean;
  addGroup: (grantee: string) => DCEvent[];
  removeGroup: (grantee: string) => DCEvent[];
  onSave: DCEvent[];
  onCancel: DCEvent[];
};

export type TemplateContentState = ProjectTemplateContentState | MapTemplateContentState;

export type ProjectTemplateContentState = {
  type: TemplateType.PROJECT;
  decisions: OptionFieldModel<TemplateItem>;
  documents: OptionFieldModel<TemplateItem>;
  attachments: OptionFieldModel<TemplateAttachment>;
  readonly hasSealedSelection: boolean;
  readonly hasSelection: boolean;
  readonly error?: TMessage;
  readonly request: UpdateProjectTemplateContentRequest;
};

export type MapTemplateContentState = {
  type: TemplateType.MAP;
  selected: OptionFieldModel<MapTemplateSetting>;
  readonly hasSelection: boolean;
  readonly error?: TMessage;
  readonly request: UpdateMapTemplateContentRequest;
};

export function showSaveProjectAsTemplateDialog({
  projectId,
  projectName,
  account,
  decisions,
  documents,
  referrer,
}: {
  projectId: string;
  projectName: string;
  account: TemplateAccount;
  decisions: TemplateItem[];
  documents: TemplateItem[];
  referrer: string;
}): DCEvent[] {
  const content = projectContent(decisions, documents);
  return saveAsTemplateDialog({
    account,
    name: projectName,
    referrer,
    content,
    saveContent: () => ({ ...content.request, projectId }),
  });
}

export function showSaveMapAsTemplateDialog({
  account,
  companyId,
  name,
  settings,
  referrer,
}: {
  account: TemplateAccount;
  companyId: string;
  name: string;
  settings: MapSettings;
  referrer: string;
}): DCEvent[] {
  const withValues = Object.values(MapTemplateSetting).filter(
    (setting) => settings.values[setting] !== undefined,
  );
  const content = mapContent(withValues, withValues);
  return saveAsTemplateDialog({
    account,
    name,
    referrer,
    content,
    saveContent: () => ({
      type: TemplateType.MAP,
      companyId,
      settings: { ...settings, values: pick(settings.values, content.request.selected) },
    }),
  });
}

function saveAsTemplateDialog({
  account,
  name,
  referrer,
  content,
  saveContent,
}: {
  account: TemplateAccount;
  name: string;
  referrer: string;
  content: TemplateContentState;
  saveContent: () => SaveTemplateContentRequest;
}): DCEvent[] {
  const details = templateDetails(account, name);
  return templateDialog({
    title: { key: "dBot.project.save-as-template.title" },
    submitLabel: {
      key: "dBot.project.save-as-template.save",
      params: { accountName: account.name },
    },
    details,
    content,
    submit: () =>
      templateApiClient.save(
        {
          ...templateDetailsRequest(details),
          enterpriseAccountId: account.id,
          content: saveContent(),
        },
        closeOnSuccess((data) => [
          navigateTo(
            {
              name: "enterprise-account",
              params: {
                id: account.id,
                tab: "content",
                accountSettingsTab: templateTypeSegments[content.type],
              },
              queryParams: { template: data.templateId },
            },
            { state: navigationState({ referrer }) },
          ),
        ]),
      ),
  });
}

export function showTemplateSettingsDialog({
  account,
  template,
  language,
  onSaved,
}: {
  account: TemplateAccount;
  template: TemplateDTO;
  language: Language;
  onSaved: (templates: TemplateDTO[]) => DCEvent[];
}): DCEvent[] {
  const details = templateDetails(account, template.name, template.description);
  const content = savedTemplateContent(template.data, language);
  return templateDialog({
    title: { key: "enterprise-account.templates.settings" },
    submitLabel: { key: "save" },
    details,
    content,
    submit: () =>
      templateApiClient.update(
        template.id,
        { ...templateDetailsRequest(details), content: content.request },
        closeOnSuccess((data: TemplateOverviewDTO) => onSaved(data.templates)),
      ),
  });
}

export function showTemplateVisibilityDialog({
  template,
  groups,
  onSaved,
}: {
  template: TemplateDTO;
  groups: AccountUserGroup[];
  onSaved: (templates: TemplateDTO[]) => DCEvent[];
}): DCEvent[] {
  const dialog: TemplateVisibilityDialogState = makeAutoObservable<TemplateVisibilityDialogState>(
    {
      type: "template-visibility",
      templateName: template.name,
      status: new RadioFieldModel<TemplateStatus>({
        value: template.status,
        options: ["ENABLED", "DISABLED"],
        idFn: (status) => status,
        labelFn: (status) => ({ key: templateStatusLabels[status] }),
      }),
      visibleTo: template.visibleTo,
      groups,
      isSaving: false,
      addGroup: (grantee) => [
        thunk(() => {
          dialog.visibleTo = [...dialog.visibleTo, grantee];
        }),
      ],
      removeGroup: (grantee) => [
        thunk(() => {
          dialog.visibleTo = dialog.visibleTo.filter((it) => it !== grantee);
        }),
      ],
      onSave: [
        thunk((dispatch) => {
          dialog.isSaving = true;
          dispatch([
            templateApiClient.updateVisibility(
              template.id,
              { status: dialog.status.value, visibleTo: dialog.visibleTo },
              closeOnSuccess((data: TemplateOverviewDTO) => onSaved(data.templates)),
            ),
          ]);
        }),
      ],
      onCancel: [{ type: "closeDialog" }],
    },
    {},
    { autoBind: true },
  );
  return [{ type: "show-dialog", dialog }];
}

function savedTemplateContent(data: TemplateDataDTO, language: Language): TemplateContentState {
  switch (data.type) {
    case TemplateType.PROJECT:
      return projectContent(
        data.matters.map((matter) => ({
          id: matter.id,
          label: getDecisionName(matter.type),
          attachments: matter.attachments,
          isSealed: false,
        })),
        data.documents.map((document) => ({
          id: document.id,
          label: templateDocumentTitle(document.title, language) ?? getDocumentLabel(document.type),
          attachments: document.attachments,
          isSealed: false,
        })),
        data.excluded,
      );
    case TemplateType.MAP:
      return mapContent(
        data.settings,
        data.settings.filter((setting) => !data.excluded.includes(setting)),
      );
  }
}

function templateDialog({
  title,
  submitLabel,
  details,
  content,
  submit,
}: {
  title: TMessage;
  submitLabel: TMessage;
  details: TemplateDetailsState;
  content: TemplateContentState;
  submit: () => DCEvent;
}): DCEvent[] {
  const dialog: SaveAsTemplateDialogState = makeAutoObservable<SaveAsTemplateDialogState>(
    {
      type: "save-as-template",
      title,
      submitLabel,
      details,
      content,
      isSaving: undefined,
      onSave: [
        thunk((dispatch) => {
          if (!details.name.validate() || !content.hasSelection) return;
          dialog.isSaving = true;
          dispatch([submit()]);
        }),
      ],
      onCancel: [{ type: "closeDialog" }],
    },
    {},
    { autoBind: true },
  );
  return [{ type: "show-dialog", dialog }];
}

function mapContent(
  options: MapTemplateSetting[],
  value: MapTemplateSetting[],
): MapTemplateContentState {
  const selected = new OptionFieldModel<MapTemplateSetting>({
    options,
    value,
    idFn: (setting) => setting,
    labelFn: (setting) => ({ key: mapTemplateSettingLabels[setting] }),
  });
  return makeAutoObservable<MapTemplateContentState>({
    type: TemplateType.MAP,
    selected,
    get hasSelection(): boolean {
      return selected.selected.length > 0;
    },
    get error(): TMessage | undefined {
      return selected.selected.length > 0 ? undefined : { key: "templates.map.nothing-selected" };
    },
    get request(): UpdateMapTemplateContentRequest {
      return { type: TemplateType.MAP, selected: selected.selected };
    },
  });
}

export const mapTemplateSettingLabels: { [setting in MapTemplateSetting]: TMessageKey } = {
  [MapTemplateSetting.FORMAT]: "ownership.format",
  [MapTemplateSetting.FILTERS]: "common.filter",
  [MapTemplateSetting.NODE_SETTINGS]: "ownership.node.settings",
  [MapTemplateSetting.THRESHOLD]: "ownership.map.settings.threshold",
  [MapTemplateSetting.MAX_ENTITIES]: "ownership.map.settings.maxnum",
};

function projectContent(
  decisions: TemplateItem[],
  documents: TemplateItem[],
  excluded?: TemplateExclusions,
): ProjectTemplateContentState {
  const decisionsField = itemField(decisions, excluded?.decisionIds);
  const documentsField = itemField(documents, excluded?.documentIds);
  const attachmentsField = attachmentField([...decisions, ...documents], excluded?.attachmentIds);
  const hasSelection = () => decisionsField.selected.length + documentsField.selected.length > 0;
  return makeAutoObservable<ProjectTemplateContentState>({
    type: TemplateType.PROJECT,
    decisions: decisionsField,
    documents: documentsField,
    attachments: attachmentsField,
    get hasSealedSelection(): boolean {
      return documentsField.selected.some((item) => item.isSealed);
    },
    get hasSelection(): boolean {
      return hasSelection();
    },
    get error(): TMessage | undefined {
      return hasSelection() ? undefined : { key: "dbot.project.save-as-template.nothing-selected" };
    },
    get request(): UpdateProjectTemplateContentRequest {
      return {
        type: TemplateType.PROJECT,
        decisionIds: decisionsField.selected.map((item) => item.id),
        documentIds: documentsField.selected.map((item) => item.id),
        attachmentIds: attachmentsField.selected.map((attachment) => attachment.id),
      };
    },
  });
}

export function templateDetails(
  account: TemplateAccount,
  name: string,
  description?: string,
): TemplateDetailsState {
  const details: TemplateDetailsState = makeAutoObservable<TemplateDetailsState>(
    {
      account: accountField(account),
      name: new StringFieldModel({ value: name, required: true }),
      description: new StringFieldModel({ value: description ?? "" }),
      hasDescription: description !== undefined,
      addDescription() {
        details.hasDescription = true;
      },
      removeDescription() {
        details.hasDescription = false;
      },
    },
    {},
    { autoBind: true },
  );
  return details;
}

export const templateDetailsRequest = (
  details: TemplateDetailsState,
): { name: string; description?: string } => ({
  name: details.name.validatedValue,
  description: details.hasDescription
    ? details.description.validatedValue.trim() || undefined
    : undefined,
});

export const templateDocumentTitle = (
  title: LocalizedText[] | undefined,
  language: Language,
): TMessage | undefined =>
  when(findLocalizedTextOrDefault({ lang: language }, title), (text) => ({ translated: text }));

function accountField(account: TemplateAccount) {
  return new RadioFieldModel<TemplateAccount>({
    value: account,
    options: [account],
    idFn: (item) => item.id,
    labelFn: (item) => ({ translated: item.name }),
  });
}

function itemField(items: TemplateItem[], excludedIds: string[] = []) {
  return new OptionFieldModel<TemplateItem>({
    options: items,
    value: items.filter((item) => !excludedIds.includes(item.id)),
    idFn: (item) => item.id,
    labelFn: (item) => item.label,
  });
}

function attachmentField(items: TemplateItem[], excludedIds: string[] = []) {
  const attachments = items.flatMap((item) => item.attachments);
  return new OptionFieldModel<TemplateAttachment>({
    options: attachments,
    value: attachments.filter((attachment) => !excludedIds.includes(attachment.id)),
    idFn: (attachment) => attachment.id,
    labelFn: (attachment) => ({ translated: attachment.filename }),
  });
}

function closeOnSuccess<T>(onSuccess: (data: T) => DCEvent[]) {
  return {
    onSuccess: (res: HttpResponse<T>): DCEvent[] => [
      { type: "closeDialog" },
      ...onSuccess(res.data),
      showSuccess({ key: "project.templates.save-success-message" }),
    ],
    onFailure: (): DCEvent[] => [{ type: "closeDialog" }, showGeneralError()],
  };
}
